import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../styles/panel/user/manageShop/EditShopCategories.css";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../../../../api/product_management/categoryApi";
import toast, { Toaster } from "react-hot-toast";
import { swalConfirm } from "../../../../utils/swalAlert";
import {
  BACK_ICON,
  CHEVRON_ICON,
  CLOSE_ICON,
  FOLDER_ICON,
  HELP_ICON,
  PLUS_ICON,
  TRASH_ICON,
  Icon,
} from "./shared/EditShopShared";

// آیکون مداد برای ویرایش — به‌صورت مستقل تعریف شده تا وابسته به فایل shared نباشد
const EDIT_ICON_PATH = (
  <>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </>
);

const GUIDE_TREE = [
  {
    icon: "🗂",
    name: "پوشاک",
    note: "دسته اصلی — بدون والد",
    children: [
      { icon: "👔", name: "مردانه", note: "والد: پوشاک" },
      { icon: "👗", name: "زنانه", note: "والد: پوشاک" },
    ],
  },
  {
    icon: "🗂",
    name: "الکترونیک",
    note: "دسته اصلی — بدون والد",
    children: [
      { icon: "📱", name: "موبایل", note: "والد: الکترونیک" },
      { icon: "💻", name: "لپ‌تاپ", note: "والد: الکترونیک" },
    ],
  },
];

export default function EditShopCategories() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const onBack = () => navigate(-1);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [showGuide, setShowGuide] = useState(false);

  const [newName, setNewName] = useState("");
  const [newParentId, setNewParentId] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editParentId, setEditParentId] = useState("");
  const [savingEditId, setSavingEditId] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await getCategories(shopId);

      let data = [];
      if (Array.isArray(res.data)) {
        data = res.data;
      } else if (Array.isArray(res.data?.results)) {
        data = res.data.results;
      }

      setCategories(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const parents = useMemo(
    () => categories.filter((c) => !c.parent),
    [categories],
  );
  const childrenByParent = useMemo(() => {
    const map = {};
    categories
      .filter((c) => c.parent)
      .forEach((c) => {
        if (!map[c.parent]) map[c.parent] = [];
        map[c.parent].push(c);
      });
    return map;
  }, [categories]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  /*
   * استخراج پیام خطا از پاسخ سرور
   */
  const getErrorMessage = (err, fallback) => {
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.detail ||
      (typeof err?.response?.data === "string" ? err.response.data : null) ||
      err?.message ||
      fallback
    );
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || adding) return;

    setAdding(true);
    try {
      const payload = {
        name,
        parent: newParentId ? Number(newParentId) : null,
        sort_order: 0,
      };
      await createCategory(shopId, payload);
      toast.success("دسته بندی با موفقیت اضافه شد");
      setNewName("");
      setNewParentId("");
      await loadCategories();
    } catch (err) {
      console.log(err);
      toast.error(
        getErrorMessage(err, "اضافه کردن دسته بندی با خطا مواجه شد :"),
      );
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (deletingId) return;
    const confirmed = await swalConfirm({
      title: "حذف دسته بندی",
      text: categoryName
        ? `آیا از حذف دسته بندی «${categoryName}» مطمئن هستی؟`
        : "آیا از حذف این دسته بندی مطمئن هستی؟",
      confirmText: "بله، حذف کن",
      cancelText: "انصراف",
      danger: true,
    });

    if (!confirmed) return;
    setDeletingId(categoryId);
    try {
      await deleteCategory(shopId, categoryId);
      toast.success("دسته بندی با موفقیت حذف شد");

      setCategories((prev) =>
        prev.filter((c) => c.id !== categoryId && c.parent !== categoryId),
      );
    } catch (err) {
      console.log(err);
      toast.error(getErrorMessage(err, "حذف کردن دسته بندی با خطا مواجه شد :"));
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setEditName(category.name || "");
    setEditParentId(category.parent ? String(category.parent) : "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditParentId("");
  };

  const handleEditSubmit = async (e, category) => {
    e.preventDefault();
    e.stopPropagation();
    const name = editName.trim();
    if (!name || savingEditId) return;

    setSavingEditId(category.id);
    try {
      const payload = {
        name,
        parent: editParentId ? Number(editParentId) : null,
      };
      await updateCategory(shopId, category.id, payload);
      toast.success("دسته‌بندی با موفقیت ویرایش شد");
      cancelEdit();
      await loadCategories();
    } catch (err) {
      console.log(err);
      toast.error(getErrorMessage(err, "ویرایش دسته‌بندی با خطا مواجه شد :"));
    } finally {
      setSavingEditId(null);
    }
  };

  return (
    <div className="shop-manage sc-wrap">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "inherit",
            direction: "rtl",
            fontSize: "13.5px",
          },
        }}
      />
      <div className="esi-head">
        <button className="esp-back-btn" onClick={onBack} aria-label="بازگشت">
          <Icon>{BACK_ICON}</Icon>
        </button>
        <div>
          <h1>دسته‌بندی محصولات</h1>
          <p>دسته‌بندی‌ها و زیردسته‌های این فروشگاه رو مدیریت کن.</p>
        </div>
        <button
          type="button"
          className="sc-help-btn"
          onClick={() => setShowGuide(true)}
          aria-label="راهنمای دسته‌بندی"
        >
          <Icon>{HELP_ICON}</Icon>
          <span>راهنما</span>
        </button>
      </div>

      {showGuide && (
        <div className="sc-guide-overlay" onClick={() => setShowGuide(false)}>
          <div className="sc-guide-card" onClick={(e) => e.stopPropagation()}>
            <div className="sc-guide-head">
              <h2>📌 راهنمای دسته‌بندی</h2>
              <button
                type="button"
                className="sc-guide-close"
                onClick={() => setShowGuide(false)}
                aria-label="بستن راهنما"
              >
                <Icon>{CLOSE_ICON}</Icon>
              </button>
            </div>

            <p className="sc-guide-desc">
              دسته‌بندی‌ها به شما کمک می‌کنند محصولاتتان را گروه‌بندی کنید تا
              مشتری راحت‌تر پیدا کند.
            </p>

            <h3 className="sc-guide-sub">📂 مثال ساختار دسته‌بندی:</h3>
            <div className="sc-guide-tree">
              {GUIDE_TREE.map((parent) => (
                <div className="sc-guide-parent" key={parent.name}>
                  <div className="sc-guide-row">
                    <span className="sc-guide-emoji">{parent.icon}</span>
                    <span className="sc-guide-name">{parent.name}</span>
                    <span className="sc-guide-note">{parent.note}</span>
                  </div>
                  {parent.children.map((child) => (
                    <div
                      className="sc-guide-row sc-guide-row--child"
                      key={child.name}
                    >
                      <span className="sc-guide-emoji">{child.icon}</span>
                      <span className="sc-guide-name">{child.name}</span>
                      <span className="sc-guide-note">{child.note}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="sc-guide-defs">
              <div className="sc-guide-def">
                <strong>بدون والد:</strong> دسته‌ای که زیر هیچ دسته دیگری نیست و
                مستقل است (مثل «پوشاک»).
              </div>
              <div className="sc-guide-def">
                <strong>والد:</strong> اگر می‌خواهید دسته‌ای زیرمجموعه یک دسته
                دیگر باشد، آن دسته را به عنوان والد انتخاب کنید (مثل «مردانه»
                زیر «پوشاک»).
              </div>
            </div>
          </div>
        </div>
      )}

      <form className="sc-add-form" onSubmit={handleAdd}>
        <div className="sc-field">
          <label className="sm-label">نام دسته‌بندی</label>
          <input
            className="sc-input"
            type="text"
            placeholder="مثلاً پوشاک مردانه"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </div>
        <div className="sc-field">
          <label className="sm-label">دسته‌بندی والد</label>
          <select
            className="sc-select"
            value={newParentId}
            onChange={(e) => setNewParentId(e.target.value)}
          >
            <option value="">بدون والد (دسته‌بندی اصلی)</option>
            {parents.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="sc-add-btn"
          disabled={adding || !newName.trim()}
        >
          <Icon>{PLUS_ICON}</Icon>
          {adding ? "در حال افزودن..." : "افزودن"}
        </button>
      </form>

      <div className="sc-list">
        {loading && <p className="sc-empty">در حال بارگذاری...</p>}

        {!loading && parents.length === 0 && (
          <p className="sc-empty">هنوز دسته‌بندی‌ای اضافه نکردی.</p>
        )}

        {!loading &&
          parents.map((parent) => {
            const children = childrenByParent[parent.id] || [];
            const isOpen = expandedIds.has(parent.id);
            return (
              <div className="sc-parent" key={parent.id}>
                {editingId === parent.id ? (
                  <form
                    className="sc-edit-form"
                    onClick={(e) => e.stopPropagation()}
                    onSubmit={(e) => handleEditSubmit(e, parent)}
                  >
                    <input
                      className="sc-input sc-edit-input"
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                    <select
                      className="sc-select sc-edit-select"
                      value={editParentId}
                      onChange={(e) => setEditParentId(e.target.value)}
                    >
                      <option value="">بدون والد (دسته‌بندی اصلی)</option>
                      {parents
                        .filter((p) => p.id !== parent.id)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="submit"
                      className="sc-edit-save"
                      disabled={savingEditId === parent.id || !editName.trim()}
                    >
                      {savingEditId === parent.id ? "..." : "ذخیره"}
                    </button>
                    <button
                      type="button"
                      className="sc-edit-cancel"
                      onClick={cancelEdit}
                    >
                      انصراف
                    </button>
                  </form>
                ) : (
                  <div
                    className={`sc-parent-row${isOpen ? " sc-parent-row--open" : ""}`}
                    onClick={() => toggleExpand(parent.id)}
                  >
                    <span className="sc-parent-icon">
                      <Icon>{FOLDER_ICON}</Icon>
                    </span>
                    <span className="sc-parent-name">{parent.name}</span>
                    {children.length > 0 && (
                      <span className="sc-count">{children.length}</span>
                    )}
                    <span
                      className={`sc-chevron${isOpen ? " sc-chevron--open" : ""}`}
                    >
                      <Icon>{CHEVRON_ICON}</Icon>
                    </span>
                    <button
                      type="button"
                      className="sc-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(parent);
                      }}
                      aria-label="ویرایش دسته‌بندی"
                    >
                      <Icon>{EDIT_ICON_PATH}</Icon>
                    </button>
                    <button
                      type="button"
                      className="sc-del-btn"
                      disabled={deletingId === parent.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(parent.id, parent.name);
                      }}
                      aria-label="حذف دسته‌بندی"
                    >
                      <Icon>{TRASH_ICON}</Icon>
                    </button>
                  </div>
                )}

                {isOpen && (
                  <div className="sc-children">
                    {children.length === 0 && (
                      <p className="sc-empty sc-empty--sub">
                        زیردسته‌ای برای این دسته‌بندی ثبت نشده.
                      </p>
                    )}
                    {children.map((child) =>
                      editingId === child.id ? (
                        <form
                          className="sc-edit-form sc-child-edit-form"
                          key={child.id}
                          onSubmit={(e) => handleEditSubmit(e, child)}
                        >
                          <input
                            className="sc-input sc-edit-input"
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                          />
                          <select
                            className="sc-select sc-edit-select"
                            value={editParentId}
                            onChange={(e) => setEditParentId(e.target.value)}
                          >
                            <option value="">بدون والد (دسته‌بندی اصلی)</option>
                            {parents.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="submit"
                            className="sc-edit-save"
                            disabled={
                              savingEditId === child.id || !editName.trim()
                            }
                          >
                            {savingEditId === child.id ? "..." : "ذخیره"}
                          </button>
                          <button
                            type="button"
                            className="sc-edit-cancel"
                            onClick={cancelEdit}
                          >
                            انصراف
                          </button>
                        </form>
                      ) : (
                        <div className="sc-child-row" key={child.id}>
                          <span className="sc-child-name">{child.name}</span>
                          <button
                            type="button"
                            className="sc-edit-btn"
                            onClick={() => startEdit(child)}
                            aria-label="ویرایش زیردسته"
                          >
                            <Icon>{EDIT_ICON_PATH}</Icon>
                          </button>
                          <button
                            type="button"
                            className="sc-del-btn"
                            disabled={deletingId === child.id}
                            onClick={() => handleDelete(child.id, child.name)}
                            aria-label="حذف زیردسته"
                          >
                            <Icon>{TRASH_ICON}</Icon>
                          </button>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
